import React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { HeightReveal } from '@/components/height-reveal';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGetPendingTeamInvitesQuery, useAcceptTeamInviteMutation } from '../services/api';
import { setTeamActiveSection } from '../redux/slices/team/teamSlice';
import { toast } from 'sonner';
import Icon from '../utils/components/Icon';

function PendingTeamInvitesBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading } = useGetPendingTeamInvitesQuery();
  const [acceptInvite, { isLoading: isAccepting }] = useAcceptTeamInviteMutation();

  const invites = data?.data ?? [];
  const hasInvites = invites.length > 0;

  const handleViewInvitations = () => {
    dispatch(setTeamActiveSection('request'));
    navigate('/team');
  };

  const handleAcceptSingleInvite = async () => {
    const invite = invites[0];
    const teamMemberId = invite?.teamMemberId;
    if (!teamMemberId) return;

    toast.info(undefined, { description: 'Accepting Team Invite' });

    const response = await acceptInvite(teamMemberId);

    if (response?.data?.success) {
      toast.success(undefined, { description: 'Team Invite Accepted' });
      window.location.reload();
    }

    if (response?.error) {
      toast.error(response?.error?.data?.errorObject?.userErrorText);
    }
  };

  const isSingleInvite = invites.length === 1;
  const singleInvite = isSingleInvite ? invites[0] : null;

  return (
    <HeightReveal show={!isLoading && hasInvites}>
      <div className="flex w-full flex-wrap items-center justify-center gap-1.5 bg-primary py-2 text-primary-foreground">
        <div className="shrink-0">
          <Icon name="UserGroupOutlined" style={{ marginRight: 4 }} />
          <span>
            {isSingleInvite ? (
              <>
                You have an invitation from{' '}
                <span className="font-semibold">{singleInvite?.teamOwnerName || 'a team'}</span>
              </>
            ) : (
              `You have ${invites.length} team invitations`
            )}
          </span>
        </div>
        <div className="shrink-0">
          {isSingleInvite ? (
            <Button
              type="button"
              variant="link"
              disabled={isAccepting}
              onClick={handleAcceptSingleInvite}
              className="text-primary-foreground"
            >
              {isAccepting && <Spinner data-icon="inline-start" />}
              <span className="underline underline-offset-4">Accept invitation</span>

              <Icon name="ArrowRightLinear" style={{ width: 14, height: 14 }} />
            </Button>
          ) : (
            <Button
              type="button"
              variant="link"
              onClick={handleViewInvitations}
              className="text-primary-foreground"
            >
              <span className="underline underline-offset-4">View invitations</span>

              <Icon name="ArrowRightLinear" style={{ width: 14, height: 14 }} />
            </Button>
          )}
        </div>
      </div>
    </HeightReveal>
  );
}

export default PendingTeamInvitesBar;
